import { useEffect, useMemo, useRef, useState } from "react";
import { Segmented } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import {
  Button,
  Dialog,
  Field,
  Input,
  Select,
  Textarea,
} from "../../../../components/ui";
import { useT } from "../../../../i18n";
import type { KhoaDich } from "../../../../i18n";
import { dungLuongVN } from "../../../../lib/dinh-dang";
import { loiTheoTruong } from "../../../../lib/errors";
import { LOAI_DON_VI, NHAN_LOAI_DON_VI, TOI_DA_MB } from "../../constants";
import type { DonVi, LoaiDonVi } from "../../types";
import type { GhiNhiemVuTay, NhiemVuPhanCong } from "../../types-phan-cong";
import { LoiForm } from "../LoiForm";
import { IconLoaiDonVi } from "./IconLoaiDonVi";

interface FormState {
  task: string;
  docTitle: string;
  type: LoaiDonVi;
  unitId: string;
  deadline: string;
  file: File | null;
}

type Loi = Partial<Record<"task" | "unit" | "deadline" | "file", string>>;

const NHAN_DOI_TUONG: Record<LoaiDonVi, { truong: KhoaDich; goiY: KhoaDich }> =
  {
    CO_QUAN: {
      truong: "vanBanPhanCong.form.coQuanNhan",
      goiY: "vanBanPhanCong.form.chonCoQuan",
    },
    PHONG_BAN: {
      truong: "vanBanPhanCong.form.phongBanNhan",
      goiY: "vanBanPhanCong.form.chonPhongBan",
    },
    CA_NHAN: {
      truong: "vanBanPhanCong.form.caNhanNhan",
      goiY: "vanBanPhanCong.form.chonCaNhan",
    },
  };

function ngayCong(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Thêm mới: giao phòng ban, hạn sau 14 ngày như bản cũ. */
const rong = (): FormState => ({
  task: "",
  docTitle: "",
  type: "PHONG_BAN",
  unitId: "",
  deadline: ngayCong(14),
  file: null,
});

/**
 * Thêm / sửa nhiệm vụ thủ công (dialog chung của bản cũ): nội dung, văn bản liên quan, tệp đính kèm,
 * loại đối tượng, hạn xử lý, đơn vị nhận lấy từ Danh sách, vai trò các cấp. Sửa mở bằng dữ liệu mới nhất
 * của nhiệm vụ; chọn tệp mới thì thay tệp cũ.
 */
export function NhiemVuTayDialog({
  open,
  nhiemVu,
  donVi,
  saving,
  loiMayChu,
  onClose,
  onSubmit,
}: {
  open: boolean;
  /** `null`: thêm mới. */
  nhiemVu: NhiemVuPhanCong | null;
  donVi: DonVi[];
  saving: boolean;
  loiMayChu: unknown;
  onClose: () => void;
  onSubmit: (vao: GhiNhiemVuTay) => void;
}) {
  const { t, locale } = useT();
  const tepRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(rong);
  const [loi, setLoi] = useState<Loi>({});

  useEffect(() => {
    if (!open) return;
    setLoi({});
    setForm(
      nhiemVu
        ? {
            task: nhiemVu.task_content,
            docTitle:
              nhiemVu.related_document ?? nhiemVu.summarization_name ?? "",
            type: nhiemVu.unit_type ?? "PHONG_BAN",
            unitId: nhiemVu.unit_id ?? "",
            deadline: nhiemVu.deadline ?? "",
            file: null,
          }
        : rong(),
    );
  }, [open, nhiemVu]);

  useEffect(() => {
    const m = loiTheoTruong(loiMayChu);
    if (Object.keys(m).length) {
      setLoi({
        task: m["/task_content"],
        unit: m["/unit_id"],
        deadline: m["/deadline"],
        file: m["/file"],
      });
    }
  }, [loiMayChu]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const doiTuong = useMemo(
    () =>
      donVi
        .filter((d) => d.type === form.type)
        .map((d) => ({ value: d.id, label: d.name })),
    [donVi, form.type],
  );

  const chonTep = (files: FileList | null) => {
    const f = files?.[0];
    if (tepRef.current) tepRef.current.value = "";
    if (!f) return;
    if (f.size > TOI_DA_MB * 1024 * 1024) {
      setLoi((l) => ({
        ...l,
        file: t("vanBanPhanCong.loi.tepQuaLon", { mb: TOI_DA_MB }),
      }));
      return;
    }
    setLoi((l) => ({ ...l, file: undefined }));
    set("file", f);
  };

  const luu = () => {
    const next: Loi = {};
    const task = form.task.trim();
    if (!task) next.task = t("vanBanPhanCong.loi.thieuNoiDung");
    if (!form.unitId) next.unit = t("vanBanPhanCong.loi.thieuDoiTuong");
    setLoi(next);
    if (Object.keys(next).length) return;
    onSubmit({
      task_content: task,
      unit_id: form.unitId,
      deadline: form.deadline,
      related_document: form.docTitle.trim(),
      file: form.file,
    });
  };

  const sua = Boolean(nhiemVu);
  const tepCu = nhiemVu?.attachment_filename ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      destroyOnHidden
      title={
        <span className="flex items-center gap-2">
          {sua ? (
            <EditOutlined className="text-primary-500" />
          ) : (
            <PlusOutlined className="text-primary-500" />
          )}
          {t(
            sua
              ? "vanBanPhanCong.form.tieuDeSua"
              : "vanBanPhanCong.form.tieuDeThem",
          )}
        </span>
      }
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("chung.huy")}
          </Button>
          <Button
            variant="primary"
            icon={<CheckOutlined />}
            loading={saving}
            onClick={luu}
          >
            {t(
              saving
                ? "chung.dangLuu"
                : sua
                  ? "vanBanChung.luuThayDoi"
                  : "vanBanPhanCong.form.nutThem",
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <LoiForm loi={loiMayChu} />
        <Field
          label={t("vanBanPhanCong.form.noiDung")}
          required
          error={loi.task}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              placeholder={t("vanBanPhanCong.form.noiDungGoiY")}
              value={form.task}
              onChange={(e) => set("task", e.target.value)}
            />
          )}
        </Field>

        <Field label={t("vanBanPhanCong.form.vanBanLienQuan")}>
          {(p) => (
            <Input
              {...p}
              placeholder={t("vanBanPhanCong.form.vanBanLienQuanGoiY")}
              value={form.docTitle}
              onChange={(e) => set("docTitle", e.target.value)}
            />
          )}
        </Field>

        <Field label={t("vanBanPhanCong.form.dinhKem")} error={loi.file}>
          {(p) => (
            <div className="space-y-2">
              <input
                ref={tepRef}
                type="file"
                className="hidden"
                tabIndex={-1}
                aria-hidden
                onChange={(e) => chonTep(e.target.files)}
              />
              <Button
                id={p.id}
                aria-describedby={p["aria-describedby"]}
                variant="outline"
                size="sm"
                icon={<PaperClipOutlined />}
                onClick={() => tepRef.current?.click()}
              >
                {t(
                  form.file
                    ? "vanBanPhanCong.form.doiTep"
                    : "vanBanPhanCong.form.chonTep",
                )}
              </Button>
              {form.file && (
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex min-w-0 max-w-full items-center gap-2 rounded border border-primary-100 bg-primary-50 px-3 py-1.5 text-xs text-primary-700">
                    <PaperClipOutlined aria-hidden />
                    <span className="truncate">{form.file.name}</span>
                    <span className="shrink-0 text-content-muted">
                      ({dungLuongVN(form.file.size, locale)})
                    </span>
                    <button
                      type="button"
                      aria-label={t("vanBanPhanCong.form.boTep", {
                        ten: form.file.name,
                      })}
                      className="rounded-sm text-danger-600 hover:text-danger-700"
                      onClick={() => set("file", null)}
                    >
                      <CloseOutlined aria-hidden />
                    </button>
                  </span>
                </div>
              )}
              {!form.file && tepCu && (
                <div className="flex flex-col gap-1">
                  <span className="inline-flex w-fit max-w-full items-center gap-2 rounded border border-default bg-subtle px-3 py-1.5 text-xs text-content-secondary">
                    <PaperClipOutlined aria-hidden />
                    <span className="truncate">{tepCu}</span>
                  </span>
                  <span className="text-2xs text-content-muted">
                    {t("vanBanPhanCong.form.dangDinhKem")}
                  </span>
                </div>
              )}
            </div>
          )}
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t("vanBanPhanCong.form.loaiDoiTuong")} required>
            {(p) => (
              <Segmented<LoaiDonVi>
                id={p.id}
                aria-describedby={p["aria-describedby"]}
                block
                className="segmented-xuong-dong"
                value={form.type}
                onChange={(v) =>
                  setForm((f) => ({ ...f, type: v, unitId: "" }))
                }
                options={LOAI_DON_VI.map((value) => ({
                  value,
                  label: t(NHAN_LOAI_DON_VI[value]),
                  icon: <IconLoaiDonVi loai={value} />,
                }))}
              />
            )}
          </Field>
          <Field label={t("vanBanPhanCong.form.hanXuLy")} error={loi.deadline}>
            {(p) => (
              <Input
                {...p}
                type="date"
                value={form.deadline}
                onChange={(e) => set("deadline", e.target.value)}
              />
            )}
          </Field>
        </div>

        <Field
          label={t(NHAN_DOI_TUONG[form.type].truong)}
          required
          error={loi.unit}
        >
          {(p) => (
            <Select
              {...p}
              showSearch
              optionFilterProp="label"
              className="w-full"
              placeholder={t(
                doiTuong.length
                  ? NHAN_DOI_TUONG[form.type].goiY
                  : "vanBanPhanCong.form.chuaCoDonVi",
              )}
              options={doiTuong}
              value={form.unitId || undefined}
              onChange={(v) => set("unitId", (v as string | undefined) ?? "")}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
